import type { AuthenticatedEmployee } from '@agos/api-client/model'

import { channelRestaurantPath } from './channel-boards'
import { formatCount } from './count'
import { tt } from './i18n'
import { surfacesFor } from './surfaces'

/**
 * What a member of staff's job is called - FRONTEND.md §9.257, FB-417.
 *
 * **The sidebar printed the key.** `AppLayout` rendered `user?.systemRole` under the signed-in name,
 * so the owner read `main_admin` and a cook read `cook`, on every screen of the manager. Legacy names
 * it in its user pill (`components/user-menu.tsx:10`) and always has.
 *
 * **Here rather than in a module**, because the shell cannot import one (§5) and because the mapping
 * already existed **twice** - in the audit-log viewer and in the reports page - while the one place
 * every user looks had neither. A third copy is how the first two would come to disagree.
 *
 * **An unknown role prints as itself.** A role the port has never seen is data, not a bug to hide:
 * showing the key is better than inventing a name for it.
 *
 * **The one spelling of the five labels** - O119 (FRONTEND.md §9.752, §9.753). A built-in role has no
 * stored name (V89 keeps `staff_roles.name` null for one, so a stored name cannot freeze one language),
 * which makes these five strings what every screen and the server's name-clash check agree on. The
 * employees roster and the profile screen read them from here; another copy is how one of them would
 * drift. Keep the five `tt(...)` lines as they are: the server refuses a custom role named like a
 * built-in (`ROLE_NAME_BUILTIN`, §10A.408), and the design keeps its list equal to these lines with a
 * test that reads them (`docs/designs/O119-custom-roles.md` §5).
 */
export function roleLabel(role: string | null | undefined): string {
  if (role === 'main_admin') return tt('Hlavný administrátor')
  if (role === 'admin') return tt('Administrátor')
  if (role === 'cook') return tt('Kuchár')
  if (role === 'waiter') return tt('Čašník')
  if (role === 'driver') return tt('Šofér')
  return role ?? '-'
}

/**
 * Where an order-taker lands after a save - `FRONTEND.md` §9.444, FB-766.
 *
 * **The owner:** *"when its Kuchár/Rozvozár it will redirect to Rozvoz nástenka, when its
 * Čašník/Časníčka to Otvorené stoly."*
 *
 * **Only one role changes and that is worth stating.** The builder sent everybody to the dispatch
 * board from four call sites; a cook and a driver want exactly that and keep it, and a waiter was
 * navigating away from it every single time.
 *
 * **`main_admin` and `admin` keep the board because the owner did not name them.** The status quo is
 * the honest default for a role their sentence does not cover - inventing a destination for the two
 * accounts that see every screen would be a guess dressed as a fix.
 *
 * **A pre-order takes precedence**, which is the behaviour one of the four sites already had: it is
 * on neither board yet, so the role question does not arise.
 *
 * One function rather than four ternaries, because the fifth call site is the one that would keep
 * the old behaviour and look fine.
 */
export function afterOrderRoute(
  systemRole: string | null | undefined,
  options: {
    preorder?: boolean
    /**
     * §9.725, FB-1025 - the slug of the order's own board (`boardChannelOf`), or `null` for none. The ordinary
     * *Otvorené stoly* leaves such a channel's orders out (§9.688), so a waiter sent there would land on the one board
     * that does not show the order just placed. Absent or `null` is `/restaurant`, as before.
     */
    ownBoard?: string | null
  } = {},
): string {
  if (options.preorder === true) return '/preorders'
  if (systemRole !== 'waiter') return '/dispatch'
  return options.ownBoard ? channelRestaurantPath(options.ownBoard) : '/restaurant'
}

/**
 * Who may hand over a shift's takings - `FRONTEND.md` §9.444, FB-767.
 *
 * **Moved here from `TableMapPage`** when the owner asked for the same control on *Otvorené stoly*.
 * It was a module-level constant on one screen, and a second screen is exactly how a role list
 * starts disagreeing with itself - `agos-one-guard-spelled-twice`.
 */
export const HANDOVER_ROLES = ['waiter', 'main_admin', 'admin'] as const

export function mayHandOverTakings(systemRole: string | null | undefined): boolean {
  return HANDOVER_ROLES.includes((systemRole ?? '') as (typeof HANDOVER_ROLES)[number])
}

/* Custom roles - FRONTEND.md §9.752 and §9.753, on BACKEND.md §10A.407 and §10A.408 (owner O119). The
 * vocabulary both screens and the shell share, so the roles screen, *Zamestnanci* and the user pill
 * cannot come to name, colour or explain a role three different ways. */

/**
 * What a custom role works as - its *Pracuje ako*, chosen at creation and never changed (§10A.407).
 *
 * The base stays in `employees.system_role` and in the token, so wherever the code decides by role a
 * custom role behaves exactly like its base. `main_admin` is never a base: V89's `staff_roles_base_check`
 * refuses it, which is what keeps a custom role off every handler only a main administrator passes.
 * In the order the screens offer them - Administrátor, Kuchár, Čašník, Šofér.
 */
export const ROLE_BASES = ['admin', 'cook', 'waiter', 'driver'] as const

export type RoleBase = (typeof ROLE_BASES)[number]

export function isRoleBase(value: string | null | undefined): value is RoleBase {
  return ROLE_BASES.some((base) => base === value)
}

/**
 * Whether a role key names a custom role - `custom_<id>`, the shape V89's `staff_roles_shape_check`
 * holds (`^custom_[0-9]+$`). A built-in is spelled by its own name, so this is the whole difference.
 */
export function isCustomRoleKey(key: string | null | undefined): boolean {
  return /^custom_[0-9]+$/.test(key ?? '')
}

/**
 * The role a person holds - `COALESCE(role_key, system_role)`, the rule the server decides by
 * (§10A.407). `roleKey` is null for a built-in role, which is every person until somebody is moved
 * into a custom one, so the base alone is the role for them.
 */
export function effectiveRoleKey(person: {
  systemRole?: string | null
  roleKey?: string | null
}): string {
  const custom = person.roleKey?.trim() ?? ''
  if (custom !== '') return custom
  return person.systemRole?.trim() ?? ''
}

/**
 * What a role is called on screen: a custom role by its own name, a built-in by `roleLabel`.
 *
 * **A built-in role's name is null on the wire** - §10A.408, *"null for a built-in, which the client
 * labels"* - so the label comes from `tt()` here and follows the language a person picked.
 *
 * **A custom role with no usable name prints its key**, as `roleLabel` prints a role it does not
 * know: a blank badge would hide that the row is wrong, and an invented name would hide it better.
 */
export function roleDisplayName(role: {
  key: string
  name?: string | null
  builtIn?: boolean
}): string {
  const own = role.name?.trim() ?? ''
  if (role.builtIn !== true && own !== '') return own
  return roleLabel(role.key)
}

/**
 * The colours a role's badge may carry - V89's `staff_roles_color_check`, in its order, which are
 * the ten `StatusBadge` draws (`BadgeColor` in `@agos/ui`, where `green` and `emerald` share one
 * palette). Spelled here because `@agos/shared` is a leaf that `@agos/ui` depends on; the two lists
 * are held equal at type level in `manager-hr`, which depends on both.
 */
export const STAFF_ROLE_COLORS = [
  'green',
  'red',
  'amber',
  'blue',
  'violet',
  'zinc',
  'orange',
  'emerald',
  'pink',
  'cyan',
] as const

export type RoleColor = (typeof STAFF_ROLE_COLORS)[number]

export function isRoleColor(value: string | null | undefined): value is RoleColor {
  return STAFF_ROLE_COLORS.some((color) => color === value)
}

/**
 * A role colour's name, for the reader - the *Farba* picker on *Nová rola* and the rename form (§9.752).
 *
 * The picker drew `green` and `cyan`, and a screen reader announced them: the values are V89's keys, which nobody on
 * the staff reads. Feminine, because the noun is *farba*; exhaustive with no `default`, so an eleventh colour is a
 * compile error here rather than a raw key on screen.
 */
export function roleColorLabel(color: RoleColor): string {
  switch (color) {
    case 'green':
      return tt('Zelená')
    case 'red':
      return tt('Červená')
    case 'amber':
      return tt('Jantárová')
    case 'blue':
      return tt('Modrá')
    case 'violet':
      return tt('Fialová')
    case 'zinc':
      return tt('Sivá')
    case 'orange':
      return tt('Oranžová')
    case 'emerald':
      return tt('Smaragdová')
    case 'pink':
      return tt('Ružová')
    case 'cyan':
      return tt('Tyrkysová')
  }
}

/**
 * The colour to draw a role's badge in: its own when it is one of the ten, else `fallback`.
 *
 * **A colour the badge does not know is not passed through.** `StatusBadge` looks its classes up
 * by the name, so an unknown one draws a badge with no colour at all, which reads as a broken
 * role rather than an odd one. Null is the ordinary case: V89 seeds the four built-ins with none,
 * so a screen passes the built-in's own colour as the fallback.
 */
export function roleBadgeColor(
  color: string | null | undefined,
  fallback: RoleColor = 'zinc',
): RoleColor {
  return isRoleColor(color) ? color : fallback
}

/**
 * What working as a base means, said from the code's own facts - the sentence under *Pracuje ako*
 * in the role editor and on the four cards of *Nová rola* (§9.752, design §2.9).
 *
 * **Two facts are read off the rules themselves rather than restated**, so they cannot outlive a
 * change to them: where a base starts (`surfacesFor`, whose first surface is the default) and
 * whether it may hand over a shift's takings (`mayHandOverTakings`). The rest are the server's,
 * named where they are decided: the waiter picker is `EmployeeService.waitersOnShift`
 * (`system_role = 'waiter'` with an open attendance), the driver picker and wallet are `Drivers`,
 * the daily e-mail is `DailyCheck`'s recipients (`main_admin`, `admin`), and the bell is
 * `maySeeNotifications` in the shell, whose premise the shell's own test holds.
 *
 * `roleListReach` is the overview's count of calls the base passes on its role alone, whatever is
 * ticked (`RoleListReach`, §10A.407). Unknown - the overview not read yet - the sentence still says
 * such calls exist, because every base has some; a count of nought says nothing.
 */
export function baseSentence(base: RoleBase, roleListReach?: number | null): string {
  const facts: string[] = []
  if (base === 'waiter') {
    facts.push(
      tt('Keď má otvorenú dochádzku, ponúka sa ako čašník pri novej objednávke a pri platbe stola.'),
    )
  }
  if (base === 'driver') {
    facts.push(
      tt('Keď je dostupný, ponúka sa dispečingu ako šofér a v peňaženke má objednávky, ktoré doviezol.'),
    )
  }
  const firstSurface = surfacesFor(base)[0]?.surface
  if (firstSurface === 'board') facts.push(tt('Po prihlásení začína na kuchynskej nástenke.'))
  if (firstSurface === 'driving') facts.push(tt('Po prihlásení začína na rozvozovej ploche.'))
  if (mayHandOverTakings(base)) facts.push(tt('Môže odovzdať tržbu zo smeny.'))
  if (base === 'admin') {
    facts.push(tt('Vidí zvonček s upozorneniami.'))
    facts.push(tt('Ak má vyplnený e-mail, dostáva denný prehľad toho, čo treba riešiť.'))
  }
  const reach = roleListReachFact(roleListReach)
  if (reach !== null) facts.push(reach)
  return facts.join(' ')
}

/** *Pri 235 volaniach rozhoduje server len podľa roly...* - locative, so one call reads *volaní*. */
function roleListReachFact(roleListReach: number | null | undefined): string | null {
  const tail = tt('rozhoduje server len podľa roly, nie podľa zaškrtnutých práv.')
  if (roleListReach == null || !Number.isFinite(roleListReach)) {
    return `${tt('Pri niektorých volaniach')} ${tail}`
  }
  const count = Math.floor(roleListReach)
  if (count <= 0) return null
  const calls = count === 1 ? tt('volaní') : tt('volaniach')
  return `${tt('Pri')} ${formatCount(count)} ${calls} ${tail}`
}

/**
 * The three fields §10A.408 adds to `GET /auth/me`'s user, read with what they mean.
 *
 * - `roleKey` - `custom_<id>` for a custom role; for a built-in, null (`employees.role_key`) or the
 *   built-in's own key - `effectiveRoleKey` and `roleDisplayName` read both alike;
 * - `roleName` - that custom role's own name, or null for a built-in, which `roleDisplayName` labels;
 * - `managesRoles` - whether the person's role manages another (an outgoing delegation edge). False for a
 *   main administrator, who reaches the roles screen anyway.
 */
export interface AuthRoleFields {
  roleKey: string | null
  roleName: string | null
  managesRoles: boolean
}

/**
 * The three role fields off the signed-in user, whatever the generated model knows yet.
 *
 * **Read through `in` rather than a cast**: today's generated `AuthenticatedEmployee` does not declare
 * these fields and the one regenerated from a spec exported after §10A.408 will. `in` narrows correctly
 * against both, where a cast would have to come out at integration and would hide a type the regenerated
 * model disagrees with; the values are checked rather than trusted either way.
 *
 * **Absent is not a yes**: a server from before the deploy answers none of them, and that reads as a
 * built-in role that manages nothing - which is exactly what such a server's people are.
 */
export function authRoleFields(user: AuthenticatedEmployee | null | undefined): AuthRoleFields {
  if (user == null) return { roleKey: null, roleName: null, managesRoles: false }
  return {
    roleKey: 'roleKey' in user ? textOrNull(user.roleKey) : null,
    roleName: 'roleName' in user ? textOrNull(user.roleName) : null,
    managesRoles: 'managesRoles' in user && user.managesRoles === true,
  }
}

function textOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null
}
